import { Money } from '@principle-theorem/accounting';
import {
  IMarketingCampaign,
  IMarketingCampaignRun,
  MarketingCampaignEntryStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  asyncForEach,
  DocumentReference,
  Firestore,
  getEnumValues,
  WithRef,
} from '@principle-theorem/shared';
import { MarketingCampaign } from './campaign';
import { MarketingCampaignRun } from './campaign-run';
import { difference } from 'lodash';

export interface ICampaignRunStatistics {
  totalEntries: number;
  activeEntries: number;
  completedEntries: number;
  convertedEntries: number;
  conversionRate: number;
  unsubscribeRate: number;
  depositsPaid: number;
  initialCostEstimate: number;
  costIncurred: number;
  remainingCostEstimate: number;
}

export class MarketingCampaignStatistics {
  static async getStatistics(
    campaignRef: DocumentReference<IMarketingCampaign>,
    campaignRunRef?: DocumentReference<IMarketingCampaignRun>
  ): Promise<ICampaignRunStatistics> {
    const campaign = asReffable(campaignRef);
    const campaignRun = campaignRunRef
      ? await Firestore.getDoc(campaignRunRef)
      : undefined;

    const nonPreviewStatuses = difference(
      getEnumValues(MarketingCampaignEntryStatus),
      [MarketingCampaignEntryStatus.Preview]
    );

    const totalEntries = await MarketingCampaign.getEntriesCount(
      campaign,
      campaignRun,
      nonPreviewStatuses
    );
    const activeEntries = await MarketingCampaign.getEntriesCount(
      campaign,
      campaignRun,
      [MarketingCampaignEntryStatus.Active]
    );
    const completedEntries = await MarketingCampaign.getEntriesCount(
      campaign,
      campaignRun,
      [MarketingCampaignEntryStatus.Completed]
    );
    const convertedEntries = await MarketingCampaign.getEntriesCount(
      campaign,
      campaignRun,
      [MarketingCampaignEntryStatus.Converted]
    );
    const unsubscribedEntries = await MarketingCampaign.getEntriesCount(
      campaign,
      campaignRun,
      [MarketingCampaignEntryStatus.Unsubscribed]
    );

    const conversionRate = convertedEntries / totalEntries;
    const unsubscribeRate = unsubscribedEntries / totalEntries;

    const costs = await MarketingCampaign.resolveCosts(campaign, campaignRun);

    const campaignRuns = await Firestore.getDocs(
      MarketingCampaign.marketingCampaignRunCol(campaign)
    );

    const depositsPaid = campaignRun
      ? await getDepositsPaid([campaignRun])
      : await getDepositsPaid(campaignRuns);

    return {
      totalEntries,
      activeEntries,
      completedEntries,
      convertedEntries,
      conversionRate,
      unsubscribeRate,
      depositsPaid,
      initialCostEstimate: costs.estimatedCost,
      costIncurred: costs.incurredCost,
      remainingCostEstimate: costs.estimatedCostRemaining,
    };
  }
}

async function getDepositsPaid(
  campaignRuns: WithRef<IMarketingCampaignRun>[]
): Promise<number> {
  const deposits = await asyncForEach(campaignRuns, (run) =>
    MarketingCampaignRun.totalPrepaymentPaid(run)
  );
  return Money.amount(Money.sum(deposits));
}
